import { type TFunction } from "i18next";
import { z } from "zod";
import { validateQuantity, validateRts, validateUnit } from "./sharedValidators";

const LANG_BASE = "vessel-call.port-services";

type BunkeringData = {
  serviceCategory?: string;
  bunkeringType?: string;
  bunkerBargeIMO?: string;
  bunkerBargeName?: string;
  bunkerBargeLookupPending?: boolean;
  bunkerBargeLookupErrorMessageKey?: string;
  rts?: string | Date | null;
  bunkeringCompanyName?: string;
  quantity?: string;
  unitOfMeasurement?: string;
};

const isBlank = (value: unknown) =>
  value === undefined || value === null || value.toString().trim() === "";

export const validateBunkeringServices = (
  data: BunkeringData,
  ctx: z.RefinementCtx,
  t: TFunction,
  currentVesselImoNumber?: string,
) => {
  const requiredFields: Array<{
    key: keyof BunkeringData;
    labelKey: string;
  }> = [
    { key: "serviceCategory", labelKey: "service-category" },
    { key: "bunkeringType", labelKey: "bunkering-type" },
    { key: "bunkerBargeIMO", labelKey: "bunker-barge-imo" },
    { key: "bunkeringCompanyName", labelKey: "bunkering-company-name" },
  ];

  requiredFields.forEach(({ key, labelKey }) => {
    if (isBlank(data[key])) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.required", {
          label: t(`${LANG_BASE}.fields.${labelKey}`),
        }),
        path: [key],
      });
    }
  });

  const bunkerBargeIMO = data.bunkerBargeIMO?.trim() ?? "";
  const hasBunkerBargeName = !isBlank(data.bunkerBargeName);
  const isBunkerBargeLookupPending = data.bunkerBargeLookupPending === true;
  const lookupErrorMessageKey =
    data.bunkerBargeLookupErrorMessageKey?.trim() || "form-validation.imo-does-not-exist";

  if (bunkerBargeIMO && bunkerBargeIMO.length > 8) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.max-length", {
        label: t(`${LANG_BASE}.fields.bunker-barge-imo`),
        max: 8,
      }),
      path: ["bunkerBargeIMO"],
    });
  }

  if (bunkerBargeIMO && bunkerBargeIMO === currentVesselImoNumber?.toString().trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("form-validation.cannot-be-same-imo"),
      path: ["bunkerBargeIMO"],
    });
  } else if (
    bunkerBargeIMO &&
    bunkerBargeIMO.length <= 8 &&
    !hasBunkerBargeName &&
    !isBunkerBargeLookupPending
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t(lookupErrorMessageKey),
      path: ["bunkerBargeIMO"],
    });
  }

  validateQuantity(data.quantity, ctx, t);
  validateUnit(data.unitOfMeasurement, ctx, t);
  validateRts(data.rts, ctx, t, 24);
};
